import { appConfig } from '../../../../config'
import { SingleUserFormDataType } from '../../../../utils/validation'
import { isLoginErrorCode } from '../../../../utils/loginErrors'
import {
  CreatedUser,
  CreateUsersResponse,
  InviteUserRequest,
  InviteUserResponse,
  User,
  UsersFromOrganisationDb,
} from './types'

export const getUsers = async ({ accessToken }: { accessToken: string }): Promise<User[]> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  const data = await res.json()
  return data
}

export const getUser = async ({ accessToken, id }: { accessToken: string; id: string }) => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/${id}`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  const data = await res.json()
  return data
}

export const deleteUser = async ({ accessToken, id }: { accessToken: string; id: string }) => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/${id}`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  const data = await res.json()
  return data
}

export const getUsersFromOrganisation = async ({
  accessToken,
  organisationId,
}: {
  accessToken: string
  organisationId: string
}): Promise<UsersFromOrganisationDb[]> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/?organisation=${organisationId}`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  const data = await res.json()
  return data
}

export const postUsersWithCvs = async ({
  accessToken,
  usersFileData,
}: {
  accessToken: string
  usersFileData: FormData
}): Promise<CreateUsersResponse> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/uploadCsv`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: usersFileData,
  })

  const data = await res.json()

  if (!res.ok) {
    throw new Error(`Request failed with status ${res.status}. Error: ${JSON.stringify(data)}`)
  }

  return data
}

export const postSingleUser = async ({
  accessToken,
  users,
}: {
  accessToken: string
  users: SingleUserFormDataType[]
}): Promise<CreatedUser[]> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(users),
  })

  const data = await res.json()

  if (!res.ok) {
    throw new Error(data?.message || 'Unknown error')
  }

  return data
}

export const logIn = async ({ accessToken }: { accessToken: string }): Promise<User> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/login`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  const data = await res.json()

  if (!res.ok) {
    if (res.status === 403 && isLoginErrorCode(data?.code)) {
      throw new Error(data.code)
    }

    throw new Error(data?.message || 'Unknown error')
  }

  return data
}

export const updateUser = async ({
  accessToken,
  id,
  newUserData,
}: {
  accessToken: string
  id: string
  newUserData: Partial<User>
}): Promise<User> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/${id}`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(newUserData),
  })
  const data = await res.json()

  if (!res.ok) {
    throw new Error(data?.message || 'Unknown error')
  }

  return data
}

export const detailUser = async ({ accessToken, newUserData }: { accessToken: string; newUserData: Partial<User> }) => {
  const cleanNewUserData = {
    ...newUserData,
    birthYear: newUserData?.birthYear && newUserData?.birthYear.toString(),
    zipCode: newUserData?.zipCode && newUserData?.zipCode.toString(),
  }
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/details`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(cleanNewUserData),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || 'Unknown error')
  }

  return data
}

export const inviteUser = async ({
  accessToken,
  data,
}: {
  accessToken: string
  data: InviteUserRequest
}): Promise<InviteUserResponse> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/users/invite`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  })

  const responseData = await res.json()

  if (!res.ok) {
    throw new Error(responseData?.message || 'Unknown error')
  }

  return responseData
}
